import type { SpaceMembershipKind } from '../types/project-types';

/**
 * The membership question, answered in ONE place.
 *
 * Since "Keep in rail" (PUBLIC_SPACE_MAIN_SHELL decision 2), `GET /api/graphs`
 * returns two different things in one list: spaces the user belongs to, and
 * public spaces they merely pinned. Presence in `projects` / `spaces` therefore
 * no longer implies membership, and every membership decision - most
 * importantly `MemberGate`'s member-vs-visitor shell choice - has to go through
 * these predicates instead of a `.find()` on the list.
 *
 * Fails toward membership on an ABSENT marker on purpose: persisted
 * `['projects']` caches written before this field existed, and the
 * create/resolve responses that omit it, are all real memberships. A followed
 * space always carries the marker explicitly.
 */
interface SpaceMembershipRef {
	membershipKind?: SpaceMembershipKind;
}

/** True ⇔ the user actually belongs to this space (owner…viewer, guest member). */
export const isMemberSpace = (space: SpaceMembershipRef): boolean =>
	(space.membershipKind ?? 'member') === 'member';

/** True ⇔ the space is in the rail only because the user pinned it. */
export const isFollowedSpace = (space: SpaceMembershipRef): boolean =>
	space.membershipKind === 'followed';

/**
 * True ⇔ this is the transient rail entry for the public space a signed-in
 * non-member currently has open (composed at render time, never persisted).
 */
export const isVisitorSpace = (space: SpaceMembershipRef): boolean =>
	space.membershipKind === 'visitor';

/** Only the spaces the user belongs to, order preserved. */
export const memberSpacesOnly = <T extends SpaceMembershipRef>(spaces: readonly T[]): T[] =>
	spaces.filter(isMemberSpace);
